// Phase 9A.2 — PublicSignalCard styles. Phase 9A.5.1 thinning.
//
// Token-only premium chrome for the curated public-ready signal card
// rendered inside the desktop Exchange shell. Reuses the same token
// families as MarketSignalCard (--sz-football for accent, --sz-mono-*,
// --sz-body-*, --sz-border-*, --sz-bg-*, --sz-radius-*, --sz-space-*).
// No hex literals, no rgb/rgba, no new tokens.
//
// 9A.5.1: file thinned. Section/grid/state moved to
// `public-signal-section.styles.ts`; drawer chrome moved to
// `public-signal-drawer.styles.ts`; control buttons + filter chips
// moved to `public-signal-controls.styles.ts`. All three are
// re-exported here so existing
// `import * as s from "./public-signal-card.styles.ts"` callers
// keep working unchanged.

import type { CSSProperties } from "react";

// ─── Card chrome ────────────────────────────────────────────────

export const card: CSSProperties = {
  background: "var(--sz-bg-surface)",
  border: "1px solid var(--sz-border-default)",
  borderRadius: "var(--sz-radius-lg)",
  padding: "var(--sz-space-4)",
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-3)",
  borderTop: "2px solid var(--sz-football)",
};

export const cardHeaderRow: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "var(--sz-space-2)",
  alignItems: "center",
  justifyContent: "space-between",
};

export const cardHeaderLeft: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "var(--sz-space-2)",
  alignItems: "center",
};

export const modulePill: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  padding: "3px 10px",
  borderRadius: "var(--sz-radius-sm)",
  border: "1px solid var(--sz-football)",
  color: "var(--sz-football)",
  background: "transparent",
};

export const labelPill: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  padding: "3px 8px",
  borderRadius: "var(--sz-radius-sm)",
  border: "1px solid var(--sz-border-default)",
  color: "var(--sz-text-secondary)",
  background: "transparent",
};

export const cardTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "var(--sz-display-sm)",
  letterSpacing: "1.5px",
  color: "var(--sz-text-primary)",
  lineHeight: 1.1,
};

export const cardSubtitle: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

export const cardSummary: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-text-secondary)",
  lineHeight: 1.5,
};

// ─── Probability grid ───────────────────────────────────────────

export const metaGrid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "max-content 1fr",
  rowGap: "var(--sz-space-1)",
  columnGap: "var(--sz-space-3)",
  alignItems: "baseline",
  paddingTop: "var(--sz-space-2)",
  borderTop: "1px solid var(--sz-border-subtle)",
};

export const metaKey: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

export const metaValue: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  letterSpacing: "1px",
  color: "var(--sz-text-secondary)",
};

export const metaValueEdgePositive: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  letterSpacing: "1px",
  color: "var(--sz-success-text)",
};

export const metaValueEdgeNegative: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-sm)",
  letterSpacing: "1px",
  color: "var(--sz-text-muted)",
};

// ─── Caution flags + footer ─────────────────────────────────────

export const cautionRow: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "var(--sz-space-2)",
};

export const cautionFlag: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  padding: "3px 8px",
  borderRadius: "var(--sz-radius-sm)",
  border: "1px solid var(--sz-border-default)",
  color: "var(--sz-text-muted)",
  background: "transparent",
};

export const truthFooter: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-text-muted)",
  fontStyle: "italic",
  lineHeight: 1.5,
};

export const generatedLine: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1px",
  color: "var(--sz-text-muted)",
};

// ─── Re-exports (Phase 9A.5.1 split) ────────────────────────────

export {
  grid,
  healthBody,
  healthDisclaimer,
  healthEyebrow,
  healthHeaderRow,
  healthMetric,
  healthPanel,
  healthTitle,
  section,
  sectionEyebrow,
  sectionHeader,
  sectionSubtitle,
  sectionTitle,
  stateBody,
  stateCard,
  stateTitle,
} from "./public-signal-section.styles.ts";

export {
  drawerBackdrop,
  drawerCloseButton,
  drawerFootnote,
  drawerHeader,
  drawerHeaderRow,
  drawerNoteActions,
  drawerNoteCounter,
  drawerNoteFooter,
  drawerNoteHint,
  drawerNoteTextarea,
  drawerPanel,
  drawerSaveError,
  drawerSaveRow,
  drawerSection,
  drawerSectionEyebrow,
  drawerTagChip,
  drawerTagsInput,
  drawerTagsRow,
} from "./public-signal-drawer.styles.ts";

export {
  filterChip,
  filterChipActive,
  filterLabel,
  filterRow,
  inspectButton,
  saveButton,
  savedButton,
} from "./public-signal-controls.styles.ts";
